<template>
  <div class="verify-page">
    <div class="verify-card">
      <div class="top-bar">
        <div class="brand">DSA<span>Deep Scholastic Assessment</span></div>
      </div>
      <h1>{{ t.verifyTitle }}</h1>
      <p class="sub">{{ t.verifySub }}</p>

      <form class="verify-form" @submit.prevent="runVerify">
        <label for="reportId">{{ t.reportId }}</label>
        <div class="row">
          <input
            id="reportId"
            v-model.trim="reportId"
            type="text"
            placeholder="DSA-TR-XXXXXXXXXX"
            autocomplete="off"
          >
          <button type="submit" class="btn btn-primary" :disabled="loading || !reportId">
            {{ loading ? t.checking : t.verifyBtn }}
          </button>
        </div>
      </form>

      <dsa-loading v-if="loading" compact :text="t.verifying" />

      <div v-else-if="error" class="result bad">
        <h2>{{ t.notVerified }}</h2>
        <p>{{ error }}</p>
      </div>

      <div v-else-if="result" class="result" :class="result.valid ? 'ok' : 'bad'">
        <h2>{{ result.valid ? t.verifiedOk : t.verifiedExpired }}</h2>
        <dl class="meta">
          <div><dt>{{ t.reportId }}</dt><dd>{{ result.reportId }}</dd></div>
          <div><dt>{{ t.candidate }}</dt><dd>{{ result.candidate && result.candidate.name }}</dd></div>
          <div><dt>{{ t.examId }}</dt><dd>{{ result.candidate && result.candidate.examId }}</dd></div>
          <div><dt>{{ t.subject }}</dt><dd>{{ result.subject && result.subject.name }} ({{ result.subject && result.subject.code }})</dd></div>
          <div><dt>{{ t.consolidatedScore }}</dt><dd>{{ format1(result.score && result.score.consolidated) }} / {{ (result.score && result.score.fullScore) || 120 }}</dd></div>
          <div><dt>{{ t.performanceLevel }}</dt><dd>{{ result.score && result.score.performanceLevel }}</dd></div>
          <div><dt>{{ t.issueDate }}</dt><dd>{{ result.reportMeta && result.reportMeta.issueDate }}</dd></div>
          <div><dt>{{ t.validUntil }}</dt><dd>{{ result.reportMeta && result.reportMeta.validUntil }}</dd></div>
        </dl>
        <div v-if="result.attempts && result.attempts.length" class="attempts">
          <h3>{{ t.disclosedAttempts }}</h3>
          <ul>
            <li v-for="a in result.attempts" :key="a.attemptNo">
              {{ a.attemptLabel || a.attemptNo }} · {{ a.window }} · {{ t.raw }} {{ format1(a.raw) }} · {{ t.credit }} {{ a.credit }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { verifyScoreReport } from '@/api/dsa'
import {
  scoreReportCopy,
} from '@/i18n/scoreReport'

export default {
  name: 'PublicVerify',
  data() {
    return {
      reportId: '',
      loading: false,
      result: null,
      error: '',
      locale: 'en',
    }
  },
  computed: {
    t() {
      return scoreReportCopy(this.locale)
    },
  },
  mounted() {
    const q = this.$route.query || {}
    if (q.report_id) this.reportId = String(q.report_id)
    else if (q.reportId) this.reportId = String(q.reportId)
    if (this.$route.params.reportId) this.reportId = String(this.$route.params.reportId)
    if (this.reportId) this.runVerify()
  },
  methods: {
    format1(n) {
      if (n == null || n === '') return '—'
      const x = Number(n)
      return Number.isNaN(x) ? String(n) : x.toFixed(1)
    },
    async runVerify() {
      if (!this.reportId) return
      this.loading = true
      this.error = ''
      this.result = null
      try {
        const res = await verifyScoreReport(this.reportId)
        if (res.data && res.data.code === 0 && res.data.data) {
          this.result = res.data.data
          if (this.$route.query.report_id !== this.reportId) {
            this.$router.replace({ path: '/verify', query: { report_id: this.reportId } }).catch(() => {})
          }
        } else {
          this.error = (res.data && res.data.msg) || this.t.reportNotFound
        }
      } catch (e) {
        this.error = this.t.verifyFailed
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.verify-page{
  min-height:100vh;display:flex;align-items:flex-start;justify-content:center;
  padding:48px 16px;background:linear-gradient(180deg,#f4f7fb 0%,#eef3f9 100%);
}
.verify-card{
  width:100%;max-width:640px;background:#fff;border:1px solid var(--line,#d8e0ea);
  border-radius:16px;padding:28px 28px 32px;box-shadow:0 10px 30px rgba(15,35,70,.06);
}
.top-bar{display:flex;justify-content:space-between;gap:12px;align-items:flex-start}
.brand{font-weight:800;font-size:22px;color:#0b3a6e}
.brand span{display:block;font-size:12px;font-weight:600;color:var(--muted,#6b7c93);letter-spacing:.04em;margin-top:2px}
h1{margin:18px 0 8px;font-size:24px}
.sub{margin:0 0 20px;color:var(--muted,#6b7c93);line-height:1.45}
.verify-form label{display:block;font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#0b3a6e;margin-bottom:8px}
.row{display:flex;gap:10px}
input{
  flex:1;border:1px solid var(--line,#d8e0ea);border-radius:10px;padding:12px 14px;font-size:15px;
}
.result{margin-top:22px;border-radius:12px;padding:16px 18px;border:1px solid var(--line,#d8e0ea)}
.result.ok{background:#f3faf6;border-color:#b7e0c5}
.result.bad{background:#fff5f5;border-color:#f0c2c2}
.result h2{margin:0 0 12px;font-size:18px}
.meta{display:grid;gap:8px;margin:0}
.meta > div{display:grid;grid-template-columns:140px 1fr;gap:8px;font-size:14px}
.meta dt{margin:0;color:var(--muted,#6b7c93)}
.meta dd{margin:0}
.attempts{margin-top:14px}
.attempts h3{margin:0 0 8px;font-size:13px;text-transform:uppercase;letter-spacing:.06em;color:#0b3a6e}
.attempts ul{margin:0;padding-left:18px;font-size:13px;line-height:1.5}
@media (max-width:640px){
  .row{flex-direction:column}
  .meta > div{grid-template-columns:1fr}
}
</style>
